import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { applyTenantTheme } from "@/lib/theme";

const AuthContext = createContext(null);

// Modules the backend actually gates through the Role permission matrix
// (server.py's make_crud(..., module=...) calls) — mirrors that set exactly.
// Every other page id keeps using the legacy `pages` grant, role_id or not.
const GATED_MODULES = [
  "leads", "customers", "quotes", "sales", "inventory",
  "visitors", "architects", "tasks", "invoice-gen", "meetplan", "petty", "calls", "analytics",
  "commissions", "cashbook", "record-contacts", "projects", "discussions", "documents",
  // api_hr.py's own permissions.py-backed modules (_can_hr/_require_hr) —
  // added so a role_id account's Payroll/Leave route+action access mirrors
  // its actual configured grant instead of falling through to the legacy
  // `pages` array, same as every module above.
  "payroll", "leave",
];

// api_hr.py's _can_hr() bypasses the role-permission check entirely for
// role === "admin" OR "accountant" on these two modules specifically — the
// literal-role floor this codebase's HR API has always had, never narrowed
// by the later permissions.py engine. Mirrored here so the UI never shows
// (or hides) an action the backend would answer differently for.
const HR_ROLE_BYPASS_MODULES = ["payroll", "leave"];

// Pages whose data comes from another module's routes (server.py checks that module): a Role must grant view on it
// as well, or the menu would offer a page whose every request is refused (the accounts login opened Stock Ledger,
// Purchase Orders and Site Survey this way). A list means any one of them.
export const PAGE_MODULES = {
  "stock-ledger": "inventory", "purchase-orders": "inventory", "manufacturer-orders": "inventory",
  "inv-analytics": "inventory", dwsurvey: "quotes", "quote-followups": "quotes", "quote-builder": "quotes",
  pipeline: "quotes", outstanding: ["sales", "invoice-gen", "cashbook"],
};

const SESSION_KEY = "crm_session";
const readSession = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking; false = anon; obj = user
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null); // entity config: branding + enabled_modules
  const [roles, setRoles] = useState([]);
  const [rolesReady, setRolesReady] = useState(false);       // the Roles list has been read (canAccess depends on it)
  // The PIN or temporary password just typed, kept in memory only until the person has chosen their own password,
  // so the "Choose your password" screen does not ask for it a second time.
  const pendingSecret = useRef("");
  // This device's sign-in (shared computer? its idle limit), from the sign-in or /auth/me.
  const [session, setSession] = useState(readSession);
  const keepSession = (value) => {
    setSession(value || null);
    try { value ? localStorage.setItem(SESSION_KEY, JSON.stringify(value)) : localStorage.removeItem(SESSION_KEY); } catch { /* storage off */ }
  };

  const loadTenant = () => api.get("/tenants/me").then((r) => setTenant(r.data)).catch(() => setTenant(null));

  // Business Settings' brand colours drive the theme, not just the record.
  useEffect(() => { applyTenantTheme(tenant); }, [tenant]);
  const loadRoles = () => api.get("/roles").then((r) => setRoles(r.data)).catch(() => setRoles([]))
    .finally(() => setRolesReady(true));

  useEffect(() => {
    const token = localStorage.getItem("crm_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        if (r.data.session !== undefined) keepSession(r.data.session);
        if (!r.data.must_set_password) { loadTenant(); loadRoles(); }
      })
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  // A session from /auth/login, /auth/google or /auth/set-password. Until the person has chosen a password
  // (must_set_password) the server opens nothing else, so the company and Roles are loaded after that.
  const adopt = (data) => {
    localStorage.setItem("crm_token", data.token);
    localStorage.setItem("crm_user", JSON.stringify(data.user));
    if (data.session !== undefined) keepSession(data.session);
    setUser(data.user);
    if (!data.user.must_set_password) { loadTenant(); loadRoles(); }
    return data.user;
  };

  // `identifier` is the username or the email on the account.
  // `shared`: "This is a shared computer" — the server signs this device out after the company's idle limit.
  const login = async (identifier, password, shared = false) => {
    const { data } = await api.post("/auth/login", { username: identifier, password, shared_device: shared });
    pendingSecret.current = data.user.must_set_password ? password : "";
    return adopt(data);
  };

  const loginWithGoogle = async (credential, shared = false) => {
    const { data } = await api.post("/auth/google", { credential, shared_device: shared });
    pendingSecret.current = "";
    return adopt(data);
  };

  /** Choose a new password; `current` defaults to the PIN / temporary password typed at this sign-in. */
  const setPassword = async (newPassword, current) => {
    const { data } = await api.post("/auth/set-password", { current: current ?? pendingSecret.current, new_password: newPassword });
    pendingSecret.current = "";
    return adopt(data);
  };
  const hasPendingSecret = () => Boolean(pendingSecret.current);

  /** Signed-in devices: end every other session; this device gets a fresh token. */
  const signOutOtherDevices = async () => {
    const { data } = await api.post("/auth/sessions/sign-out-others");
    localStorage.setItem("crm_token", data.token);
    return data.ended;
  };

  // `reason` (e.g. idle sign-out) is shown once on the sign-in screen.
  const logout = (reason) => {
    pendingSecret.current = "";
    const token = localStorage.getItem("crm_token");
    if (token) {             // ends the session server-side too (the header is set here: the token is cleared below)
      api.post("/auth/logout", null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    if (typeof reason === "string" && reason) {
      try { sessionStorage.setItem("madio.signedOutReason", reason); } catch { /* storage off */ }
    }
    keepSession(null);
    localStorage.removeItem("crm_token");
    localStorage.removeItem("crm_user");
    setUser(false);
    setTenant(null);
    window.location.href = "/login";
  };

  // A Role's own grant for a module (no page fallback): admins always; legacy (no role_id) accounts as before.
  const roleGrants = (moduleId, action = "view") => {
    if (!user || user.role === "admin") return Boolean(user);
    if (!user.role_id) return true;
    const role = roles.find((r) => r.id === user.role_id);
    return !!role?.permissions?.find((p) => p.module === moduleId)?.[action];
  };

  /** True when the Role covers ALL records of one of `modules` (the server's company_wide reports). */
  const canCompanyWide = (modules) => {
    if (!user || user.needs_role) return false;
    if (user.role === "admin") return true;
    if (!user.role_id) return true;
    const role = roles.find((r) => r.id === user.role_id);
    return [].concat(modules).some((m) => {
      const p = role?.permissions?.find((x) => x.module === m);
      return !!p?.view && (p.scope || "own") === "all";
    });
  };

  // A list of page ids means any of them (a page several roles share).
  const canAccess = (pageId) => {
    if (Array.isArray(pageId)) return pageId.some((p) => canAccess(p));
    if (!user) return false;
    // No valid Role (and the administrator has not switched the transition off): the server refuses
    // every module, so the screens show none rather than pages that would answer 403.
    if (user.needs_role) return false;
    if (tenant && Array.isArray(tenant.enabled_modules) && !tenant.enabled_modules.includes(pageId)) return false;
    if (user.role === "admin") return true;
    if (user.role === "accountant" && HR_ROLE_BYPASS_MODULES.includes(pageId)) return true;
    if (user.role_id && GATED_MODULES.includes(pageId)) {
      const role = roles.find((r) => r.id === user.role_id);
      const modPerm = role?.permissions?.find((p) => p.module === pageId);
      return !!modPerm?.view;
    }
    if (user.role_id && PAGE_MODULES[pageId] && ![].concat(PAGE_MODULES[pageId]).some((m) => roleGrants(m))) return false;
    if (user.pages == null) return true;
    return Array.isArray(user.pages) && user.pages.includes(pageId);
  };

  // Per-action counterpart to canAccess (which only ever checks "view").
  // Mirrors permissions.py's LEGACY_IMPLICIT_ACTIONS server-side: an account
  // with role_id set and a gated module is checked against that specific
  // action's grant; a legacy account (no role_id) or an ungated module keeps
  // canAccess's all-or-nothing view grant for create/edit/delete, but never
  // for approve/export — those never existed in the old pages-only system.
  const canDo = (moduleId, action) => {
    if (action === "view") return canAccess(moduleId);
    if (!canAccess(moduleId)) return false;
    if (user.role === "admin") return true;
    if (user.role === "accountant" && HR_ROLE_BYPASS_MODULES.includes(moduleId)) return true;
    if (user.role_id && GATED_MODULES.includes(moduleId)) {
      const role = roles.find((r) => r.id === user.role_id);
      const modPerm = role?.permissions?.find((p) => p.module === moduleId);
      return !!modPerm?.[action];
    }
    return action !== "approve" && action !== "export";
  };

  // Landing (cost) prices: admin, accounts, or "Can see landing price" in
  // Role Manager — the same rule as the server's _can_see_cost_prices.
  const canSeeCost = !!user && (user.role === "admin" || user.role === "accountant" || !!user.can_view_cost);

  return (
    <AuthContext.Provider value={{ user, loading, tenant, roles, refreshTenant: loadTenant, refreshRoles: loadRoles, login, loginWithGoogle, setPassword, hasPendingSecret, signOutOtherDevices, session, logout, canAccess, canDo,
      canCompanyWide, canSeeCost, rolesReady }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
